/**
 * The made-to-measure models of a product line, picked in the tree at the left.
 * Admin only.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faCircleNotch,
  faPencilAlt,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import {
  DMS_PRODUCTLINE,
  getDmsProductLineLabel,
  MTM_ROUTE_PATH,
  PLATFORM,
  ROLES,
} from '@/shared/constants';
import { getUserProfile } from '@/features/auth/session';
import { hardNavigate } from '@/shared/lib/navigation';
import { TopMenu } from '@/shared/layout/TopMenu';
import { SortableHeader } from '@/shared/ui/SortableHeader';
import { ConfirmDialog } from '@/shared/ui/ConfirmDialog';
import { snackbar } from '@/shared/ui/snackbar';
import { useDeleteItem, useSavedDialogClose } from '../use-referential';
import plusIcon from '@/assets/images/plus.svg';
import { deleteDmsItem, dmsItemsQueryKey, dmsItemsQueryOptions } from '../api';
import {
  buildDmsTree,
  isDmsProductLineEntry,
  type DmsFlatNode,
} from '../dms-tree';
import { DmsProductLineTree } from '../DmsProductLineTree';
import { displayAvailability, nextListSort } from '../list';
import {
  displayedModels,
  hasAllPictures,
  modelDataSchema,
  type DmsModel,
  type ModelSort,
  type ModelSortField,
} from './model-form';
import { ModelDialog } from './ModelDialog';

const MIN_ROWS = 5;

export function ModelsPage() {
  const user = getUserProfile();
  const isAdmin = user?.role === ROLES.ADMIN;
  const queryClient = useQueryClient();

  useEffect(() => {
    if (user && !isAdmin) hardNavigate(`/${MTM_ROUTE_PATH}/main`);
  }, [user, isAdmin]);

  const models = useQuery({
    ...dmsItemsQueryOptions('model', modelDataSchema),
    enabled: isAdmin,
  });
  useEffect(() => {
    if (models.isError)
      snackbar('Failed to load items, please try again later');
  }, [models.isError]);

  const [productLine, setProductLine] = useState<string>(DMS_PRODUCTLINE.SUIT);
  const [sort, setSort] = useState<ModelSort>({ by: 'model', reverse: false });
  const [dialog, setDialog] = useState<{ item: DmsModel | null } | null>(null);
  const [removing, setRemoving] = useState<DmsModel | null>(null);

  const items = useMemo(() => models.data ?? [], [models.data]);
  const tree = useMemo(
    () => buildDmsTree(items, (item) => item.data.model ?? ''),
    [items],
  );
  const displayed = displayedModels(items, productLine, sort);

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: dmsItemsQueryKey('model') });

  const remove = useDeleteItem(
    (item: DmsModel) => deleteDmsItem(item.id),
    refresh,
  );

  const onNodeClick = (node: DmsFlatNode<unknown>) => {
    if (node.level !== 0 || !isDmsProductLineEntry(node.data)) return;
    if (node.data.value && node.data.value !== productLine)
      setProductLine(node.data.value);
  };

  const closeDialog = useSavedDialogClose(() => setDialog(null), refresh);

  if (!isAdmin) return null;

  const sortableHeader = (
    key: ModelSortField,
    label: string,
    testId: string,
  ) => (
    <SortableHeader
      label={label}
      width="10%"
      testId={`mtm-model-header-${testId}`}
      active={sort.by === key}
      descending={sort.reverse}
      onClick={() => setSort((current) => nextListSort(current, key))}
    />
  );

  return (
    <div className="container summary">
      <TopMenu platform={PLATFORM.DMS} />
      <div className="bs-row my-12" style={{ flex: 1 }}>
        <div className="col-span-2">
          <DmsProductLineTree
            data={tree}
            selectedParent={productLine}
            onNodeClick={onNodeClick}
          />
        </div>
        <div className="col-span-10 flex flex-col">
          <div className="summary-table">
            <button
              type="button"
              className="add-btn flex items-center justify-center self-center"
              style={{ zIndex: 2 }}
              data-testid="header-add-model"
              onClick={() => setDialog({ item: null })}
            >
              <img src={plusIcon} alt="" />
              <span className="pl-2">Add a new model</span>
            </button>
            <h3
              className="mb-4"
              style={{ marginTop: -24 }}
              data-testid="mtm-model-product-line-title"
            >
              {getDmsProductLineLabel(productLine)}
            </h3>
            <div className="table-container">
              <div
                className="items-container"
                role="region"
                aria-label="Models"
              >
                <table className="table items">
                  <thead>
                    <tr>
                      {sortableHeader('model', 'Model Name', 'model')}
                      {sortableHeader(
                        'modelStyleCodePart',
                        'Model Code',
                        'model-code',
                      )}
                      {sortableHeader('fit', 'Fit', 'fit')}
                      {sortableHeader(
                        'displayAvailability',
                        'Availability',
                        'availability',
                      )}
                      <th style={{ width: '20%' }}>Sizes</th>
                      <th style={{ width: '30%' }}>Description</th>
                      <th style={{ width: '10%' }}>Picture</th>
                      <th style={{ width: '15%' }}>Evening offer</th>
                      <th style={{ width: '10%' }} className="edit-header">
                        actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayed.map((item) => (
                      <tr key={item.id} className="user-row">
                        <td
                          style={{ width: '10%' }}
                          title={item.data.model ?? ''}
                          data-testid="mtm-model-row-model"
                        >
                          {item.data.model}
                        </td>
                        <td
                          style={{ width: '10%' }}
                          title={item.data.modelStyleCodePart ?? ''}
                          data-testid="mtm-model-row-model-code"
                        >
                          {item.data.modelStyleCodePart}
                        </td>
                        <td
                          style={{ width: '10%' }}
                          title={item.data.fit ?? ''}
                          data-testid="mtm-model-row-fit"
                        >
                          {item.data.fit}
                        </td>
                        <td
                          style={{ width: '10%' }}
                          title={displayAvailability(item.data)}
                          data-testid="mtm-model-row-availability"
                        >
                          {displayAvailability(item.data)}
                        </td>
                        <td
                          style={{ width: '20%' }}
                          title={item.data.sizes ?? ''}
                          data-testid="mtm-model-row-sizes"
                        >
                          {item.data.sizes}
                        </td>
                        <td
                          style={{ width: '30%' }}
                          title={item.data.description ?? ''}
                          data-testid="mtm-model-row-description"
                        >
                          {item.data.description}
                        </td>
                        <td
                          style={{ width: '10%' }}
                          data-testid="mtm-model-row-picture"
                        >
                          {hasAllPictures(item.data, productLine) && (
                            <FontAwesomeIcon
                              icon={faCheck}
                              aria-label="Pictures set"
                            />
                          )}
                        </td>
                        <td
                          style={{ width: '15%' }}
                          data-testid="mtm-model-row-evening-offer"
                        >
                          {item.data.eveningOffer && (
                            <FontAwesomeIcon
                              icon={faCheck}
                              aria-label="Evening offer"
                            />
                          )}
                        </td>
                        <td style={{ width: '10%' }} className="edit-action">
                          <button
                            type="button"
                            className="action-icon"
                            aria-label={`Edit ${item.data.model ?? ''}`}
                            data-testid="mtm-model-row-edit-button"
                            onClick={() => setDialog({ item })}
                          >
                            <FontAwesomeIcon icon={faPencilAlt} />
                          </button>
                          <button
                            type="button"
                            className="action-icon ml-2"
                            aria-label={`Remove ${item.data.model ?? ''}`}
                            data-testid="mtm-model-row-delete-button"
                            onClick={() => setRemoving(item)}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {Array.from(
                      { length: Math.max(0, MIN_ROWS - displayed.length) },
                      (_, index) => (
                        <tr key={`empty-${index}`} className="empty-row">
                          <td style={{ width: '10%' }} />
                          <td style={{ width: '10%' }} />
                          <td style={{ width: '10%' }} />
                          <td style={{ width: '20%' }} />
                          <td style={{ width: '30%' }} />
                          <td style={{ width: '10%' }} />
                          <td style={{ width: '10%' }} />
                        </tr>
                      ),
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
      {models.isFetching && (
        <FontAwesomeIcon
          icon={faCircleNotch}
          spin
          className="loading-icon"
          aria-label="Loading"
        />
      )}
      {dialog && (
        <ModelDialog
          key={dialog.item?.id ?? 'new'}
          item={dialog.item}
          productLine={productLine}
          onClose={closeDialog}
        />
      )}
      {removing !== null && (
        <ConfirmDialog
          title="Are you sure?"
          text="This action cannot be undone. This item will be deleted immediately."
          onConfirm={() => {
            if (removing) remove.mutate(removing);
            setRemoving(null);
          }}
          onCancel={() => setRemoving(null)}
        />
      )}
    </div>
  );
}
